import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Badge, Modal, Form, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import jsPDF from "jspdf";

const GRADE_THRESHOLDS = [
  { min: 80, grade: 4 },
  { min: 75, grade: 3.5 },
  { min: 70, grade: 3 },
  { min: 65, grade: 2.5 },
  { min: 60, grade: 2 },
  { min: 55, grade: 1.5 },
  { min: 50, grade: 1 },
  { min: 0,  grade: 0 },
];

const calculateGrade = (score) => {
  if (score === null || score === undefined || score === '') return null;
  const s = parseFloat(score);
  if (isNaN(s)) return null;
  for (const t of GRADE_THRESHOLDS) {
    if (s >= t.min) return t.grade;
  }
  return 0;
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const safeFileName = (value) => String(value || '')
  .replace(/[\\/:*?"<>|]/g, '')
  .replace(/\s+/g, '_')
  .slice(0, 80);

export default function ViewStudentGrade() {
  const { study_id, year_id } = useParams();
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) { thead.classList.remove('table-secondary'); thead.classList.add('table-primary'); }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) { thead.classList.remove('table-primary'); thead.classList.add('table-secondary'); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [data, setData]               = useState([]);
  const [student, setStudent]         = useState(null);
  const [yearDisplay, setYearDisplay] = useState('');

  const [showModal, setShowModal]     = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData]     = useState(null);
  const [score, setScore]             = useState('');
  const [waiting, setWaiting]         = useState(false);

  // load year label
  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      const found = res.data.find(y => String(y.year_id) === String(year_id));
      if (found) setYearDisplay(`ปีการศึกษา ${found.year_year}`);
    })
    .catch(() => {});
  }, [year_id]);

  const loadData = useCallback(() => {
    setLoading(true);
    axiosInstance.get(
      `${process.env.REACT_APP_API}/getStudentGradeDetail/${study_id}/${year_id}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    )
    .then(res => {
      const rows = res.data;
      if (rows.length > 0) {
        setStudent({
          st_idnumber: rows[0].st_idnumber,
          st_name:     rows[0].st_name,
          st_status:   rows[0].st_status,
          c_id:        rows[0].tb_class_c_id,
          r_no:        rows[0].r_no,
        });
      }
      setData(rows);
    })
    .catch(err => {
      if (err.response?.status !== 404) {
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || err.message });
      }
      setData([]);
    })
    .finally(() => setLoading(false));
  }, [study_id, year_id, setLoading]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEditClick = (row) => {
    setShowModal(true);
    setModalLoading(true);
    setModalData(null);

    axiosInstance.post(`${process.env.REACT_APP_API}/prepareGradeEdit`, {
      st_id:    row.st_id,
      teach_id: row.teach_id,
      sub_id:   row.sub_id,
      study_id,
    }, { headers: { Authorization: `Bearer ${getToken()}` } })
    .then(res => {
      const d = res.data;
      setModalData({ ...d, teach_id: row.teach_id, sub_id: row.sub_id });
      setScore(d.g_grade !== null && d.g_grade !== undefined ? String(d.g_grade) : '');
      setWaiting(d.g_waiting === '1');
    })
    .catch(err => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถโหลดข้อมูลได้' });
      setShowModal(false);
    })
    .finally(() => setModalLoading(false));
  };

  const handleSave = () => {
    setModalLoading(true);
    axiosInstance.put(`${process.env.REACT_APP_API}/updateGrade`, {
      g_id:      modalData.g_id,
      g_grade:   score,
      g_waiting: waiting ? '1' : '0',
      update_by: String(getUserId()),
    }, { headers: { Authorization: `Bearer ${getToken()}` } })
    .then(() => {
      const newScore   = score === '' ? null : parseFloat(score);
      const newWaiting = waiting ? '1' : '0';
      setData(prev => prev.map(r =>
        r.teach_id === modalData.teach_id && r.sub_id === modalData.sub_id
          ? { ...r, g_grade: newScore, g_waiting: newWaiting, g_id: modalData.g_id }
          : r
      ));
      setShowModal(false);
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
    })
    .catch(err => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถบันทึกได้' });
    })
    .finally(() => setModalLoading(false));
  };

  const previewGrade = calculateGrade(score);

  const getGradeDisplay = (row) => {
    if (row.g_id === null || row.g_id === undefined) return '-';
    if (row.g_waiting === '1') return 'ร';
    const grade = calculateGrade(row.g_grade);
    return grade !== null ? grade : '-';
  };

  const getExportRows = (semester) => data.filter(r => String(r.sub_semester) === String(semester));

  const buildExportHtml = () => {
    const studentName = student?.st_name || '';
    const studentNo = student?.st_idnumber || '';
    const roomText = student ? `ม.${student.c_id}/${student.r_no}` : '';

    const buildTable = (semester) => {
      const rows = getExportRows(semester);
      const body = rows.length > 0
        ? rows.map((r, index) => `
          <tr>
            <td class="center">${index + 1}</td>
            <td class="center">${escapeHtml(r.sub_subid)}</td>
            <td>${escapeHtml(r.sub_subname)}${r.sub_type === 'extra' ? ' <span class="muted">(เพิ่มเติม)</span>' : ''}</td>
            <td class="center">${escapeHtml(r.teacher_name)}</td>
            <td class="center">${r.g_grade !== null && r.g_grade !== undefined ? escapeHtml(r.g_grade) : '-'}</td>
            <td class="center">${escapeHtml(getGradeDisplay(r))}</td>
          </tr>
        `).join('')
        : '<tr><td colspan="6" class="center muted">ไม่พบข้อมูล</td></tr>';

      return `
        <h3>ภาคเรียนที่ ${semester}</h3>
        <table>
          <thead>
            <tr>
              <th style="width:38px;">ที่</th>
              <th style="width:88px;">รหัสวิชา</th>
              <th>ชื่อวิชา</th>
              <th style="width:120px;">ครูผู้สอน</th>
              <th style="width:70px;">คะแนน</th>
              <th style="width:80px;">ผลการเรียน</th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>
      `;
    };

    return `
      <div class="grade-export">
        <div class="header">
          <h2>ผลการเรียนรายบุคคล</h2>
          <div>${escapeHtml(yearDisplay)}</div>
          <div>
            <strong>เลขประจำตัว:</strong> ${escapeHtml(studentNo)}
            &nbsp;&nbsp;<strong>ชื่อ-สกุล:</strong> ${escapeHtml(studentName)}
            &nbsp;&nbsp;<strong>ชั้น:</strong> ${escapeHtml(roomText)}
          </div>
        </div>
        ${buildTable('1')}
        <div class="spacer"></div>
        ${buildTable('2')}
      </div>
    `;
  };

  const exportStyles = `
    body { font-family: Arial, Tahoma, sans-serif; font-size: 12px; color: #111; }
    .grade-export { padding: 14px; background: #fff; }
    .header { text-align: center; margin-bottom: 12px; line-height: 1.6; }
    .header h2 { margin: 0 0 4px; font-size: 18px; }
    h3 { margin: 12px 0 6px; font-size: 14px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #555; padding: 5px 7px; vertical-align: middle; }
    th { background: #e9f2ff; text-align: center; }
    .center { text-align: center; }
    .muted { color: #6c757d; }
    .spacer { height: 12px; }
  `;

  const handlePrint = () => {
    if (!student || data.length === 0) return;
    const win = window.open('', '_blank', 'width=1100,height=800');
    win.document.write(`
      <html>
        <head>
          <title>ผลการเรียน_${escapeHtml(student.st_idnumber)}</title>
          <style>${exportStyles}@media print { @page { margin: 10mm; } }</style>
        </head>
        <body onload="window.print()">${buildExportHtml()}</body>
      </html>
    `);
    win.document.close();
  };

  const handleExportPDF = async () => {
    if (!student || data.length === 0) return;
    const html2canvas = (await import('html2canvas')).default;
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:fixed;left:-9999px;top:0;background:#fff;width:960px;';
    wrapper.innerHTML = `<style>${exportStyles}</style>${buildExportHtml()}`;
    document.body.appendChild(wrapper);

    const canvas = await html2canvas(wrapper, { scale: 2, useCORS: true });
    document.body.removeChild(wrapper);

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let y = 5;
    let remaining = imgHeight;

    while (remaining > 0) {
      pdf.addImage(imgData, 'PNG', 5, y, imgWidth, imgHeight);
      remaining -= (pageHeight - 10);
      if (remaining > 0) {
        pdf.addPage();
        y = 5 - (imgHeight - remaining);
      }
    }

    pdf.save(`ผลการเรียน_${safeFileName(student.st_idnumber)}_${safeFileName(student.st_name)}.pdf`);
  };

  const handleExportExcel = async () => {
    if (!student || data.length === 0) return;
    const XLSX = await import('xlsx');
    const rows = [];

    rows.push(['ผลการเรียนรายบุคคล']);
    rows.push(['ปีการศึกษา', yearDisplay.replace(/^ปีการศึกษา\s*/, '')]);
    rows.push(['เลขประจำตัว', student.st_idnumber, 'ชื่อ-สกุล', student.st_name, 'ชั้น', `ม.${student.c_id}/${student.r_no}`]);
    rows.push([]);

    ['1', '2'].forEach(semester => {
      rows.push([`ภาคเรียนที่ ${semester}`]);
      rows.push(['ที่', 'รหัสวิชา', 'ชื่อวิชา', 'ประเภท', 'ครูผู้สอน', 'คะแนน', 'ผลการเรียน']);
      const semRows = getExportRows(semester);
      if (semRows.length > 0) {
        semRows.forEach((r, index) => {
          rows.push([
            index + 1,
            r.sub_subid || '',
            r.sub_subname || '',
            r.sub_type === 'extra' ? 'เพิ่มเติม' : 'พื้นฐาน',
            r.teacher_name || '',
            r.g_grade ?? '',
            getGradeDisplay(r),
          ]);
        });
      } else {
        rows.push(['ไม่พบข้อมูล']);
      }
      rows.push([]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 8 }, { wch: 14 }, { wch: 34 }, { wch: 12 }, { wch: 24 }, { wch: 10 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ผลการเรียน');
    XLSX.writeFile(wb, `ผลการเรียน_${safeFileName(student.st_idnumber)}_${safeFileName(student.st_name)}.xlsx`);
  };

  const renderSemesterTable = (semester) => {
    const rows = data.filter(r => String(r.sub_semester) === String(semester));
    return (
      <Table responsive hover bordered size="sm" className="mb-0">
        <thead>
          <tr className="text-center">
            <th colSpan={6}>ภาคเรียนที่ {semester}</th>
          </tr>
          <tr>
            <th className="text-center" style={{ width: 90 }}>รหัสวิชา</th>
            <th>ชื่อวิชา</th>
            <th className="text-center" style={{ width: 110 }}>ครูผู้สอน</th>
            <th className="text-center" style={{ width: 70 }}>คะแนน</th>
            <th className="text-center" style={{ width: 80 }}>ผลการเรียน</th>
            <th className="text-center" style={{ width: 70 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? rows.map(r => {
            const grade = calculateGrade(r.g_grade);
            return (
              <tr key={`${r.teach_id}-${r.sub_id}`}>
                <td className="text-center">{r.sub_subid}</td>
                <td>
                  {r.sub_subname}
                  {r.sub_type === 'extra' && <span className="text-muted ms-1" style={{ fontSize: '0.8em' }}>(เพิ่มเติม)</span>}
                </td>
                <td className="text-center">{r.teacher_name}</td>
                <td className="text-center" style={{ color: 'blue' }}>
                  {r.g_grade !== null ? r.g_grade : '-'}
                </td>
                <td className="text-center">
                  {r.g_id === null
                    ? '-'
                    : r.g_waiting === '1'
                      ? <span style={{ color: 'red', fontWeight: 'bold' }}>ร</span>
                      : grade !== null
                        ? <Badge bg={grade >= 2 ? 'success' : grade >= 1 ? 'warning' : 'danger'}>{grade}</Badge>
                        : '-'
                  }
                </td>
                <td className="text-center">
                  <Button variant="link" size="sm" className="p-0" onClick={() => handleEditClick(r)}>
                    <i className="ri-edit-2-fill"></i> แก้ไข
                  </Button>
                </td>
              </tr>
            );
          }) : (
            <tr><td colSpan={6} className="text-center text-muted">ไม่พบข้อมูล</td></tr>
          )}
        </tbody>
      </Table>
    );
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ที่ปรึกษาห้องเรียน</Link></li>
              <li className="breadcrumb-item"><Link to="/advisor/student-grade">ผลการเรียนรายบุคคล</Link></li>
              <li className="breadcrumb-item active">
                {student ? student.st_name : '...'}
              </li>
            </ol>
            <h4 className="main-title mb-0">
              {student
                ? <><i className="ri-user-line me-1"></i>{student.st_name}</>
                : 'ผลการเรียนรายบุคคล'}
            </h4>
            {student && (
              <div className="text-muted fs-sm mt-1">
                เลขประจำตัว: {student.st_idnumber}
                &ensp;ชั้น ม.{student.c_id}/{student.r_no}
              </div>
            )}
          </div>
          <div className="d-flex flex-column align-items-sm-end gap-2 mt-2 mt-sm-0">
            {yearDisplay && (
              <span className="badge bg-primary fs-6 fw-normal">{yearDisplay}</span>
            )}
            <div className="d-flex flex-wrap justify-content-sm-end gap-2">
              <Button variant="outline-primary" size="sm" onClick={handlePrint} disabled={!student || data.length === 0}>
                <i className="ri-printer-line me-1"></i>พิมพ์
              </Button>
              <Button variant="outline-danger" size="sm" onClick={handleExportPDF} disabled={!student || data.length === 0}>
                <i className="ri-file-pdf-line me-1"></i>Export PDF
              </Button>
              <Button variant="outline-success" size="sm" onClick={handleExportExcel} disabled={!student || data.length === 0}>
                <i className="ri-file-excel-2-line me-1"></i>Export Excel
              </Button>
            </div>
          </div>
        </div>

        <Row className="g-3">
          <Col xl="6">
            <Card className="card-one">
              <Card.Body className="p-2">
                {renderSemesterTable('1')}
              </Card.Body>
            </Card>
          </Col>
          <Col xl="6">
            <Card className="card-one">
              <Card.Body className="p-2">
                {renderSemesterTable('2')}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="mt-3 d-flex justify-content-end">
          <Link to="/advisor/student-grade" className="btn btn-secondary btn-sm">
            <i className="ri-arrow-left-line me-1"></i>ย้อนกลับ
          </Link>
        </div>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="sm" centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6">
              <i className="ri-edit-2-fill me-1"></i>แก้ไขคะแนน
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalLoading && !modalData ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> กำลังโหลด...
              </div>
            ) : modalData && (
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">นักเรียน</Form.Label>
                  <Form.Control size="sm" value={student ? `${student.st_idnumber} ${student.st_name}` : ''} disabled />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">วิชา</Form.Label>
                  <Form.Control size="sm" value={`${modalData.sub_subid || ''} ${modalData.sub_subname || ''}`} disabled />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">
                    คะแนน
                    {previewGrade !== null && score !== '' && (
                      <span className="ms-2 text-muted fw-normal">
                        → ผลการเรียน: <strong style={{ color: 'blue' }}>{previewGrade}</strong>
                      </span>
                    )}
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={score}
                    onChange={e => setScore(e.target.value)}
                    placeholder="กรอกคะแนน 0–100"
                    autoFocus
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  id="waiting-check-student-grade"
                  label={<>ติดค้าง กรณีที่นักเรียนติด <span style={{ color: 'red', fontWeight: 'bold' }}>ร</span></>}
                  checked={waiting}
                  onChange={e => setWaiting(e.target.checked)}
                />
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer className="py-2">
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
              <i className="ri-close-line"></i> ปิด
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={handleSave}
              disabled={modalLoading || !modalData}
            >
              {modalLoading
                ? <Spinner animation="border" size="sm" />
                : <><i className="ri-check-line"></i> บันทึก</>}
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
